import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

// ---------------------------------------------------------------------------
// The visitor's chosen location.
//
// This lives above the page rather than inside it because the control that
// sets it now sits in the navbar, while the thing it affects — the ordering
// and framing of the listings — lives in the home page. Two components, one
// value, so it belongs here rather than being threaded through props.
//
// Remembered across visits the way a delivery app remembers your address:
// re-entering your neighbourhood on every visit is a tax on the returning
// user, who is exactly the user worth keeping.
// ---------------------------------------------------------------------------

const LOCATION_STORAGE_KEY = 'volcyy_location';

const LocationContext = createContext({ location: null, setLocation: () => {} });

function loadStoredLocation() {
  try {
    const raw = localStorage.getItem(LOCATION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.coords?.lat != null && parsed?.coords?.lng != null && parsed?.label) return parsed;
  } catch { /* ignore malformed/blocked storage */ }
  return null;
}

export function LocationProvider({ children }) {
  const [location, setLocationState] = useState(loadStoredLocation);

  // `null` clears it and goes back to showing everywhere.
  const setLocation = useCallback((next) => {
    setLocationState(next);
    try {
      if (next) localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(next));
      else localStorage.removeItem(LOCATION_STORAGE_KEY);
    } catch { /* ignore blocked storage */ }
  }, []);

  const value = useMemo(() => ({ location, setLocation }), [location, setLocation]);
  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
}

export function useSearchLocation() {
  return useContext(LocationContext);
}
